/**
 * The application shell.
 *
 * Below NARROW_QUERY the sidebar becomes an off-canvas drawer. It is the same
 * element in the same place in the DOM -- only CSS changes. A second,
 * phone-only nav would mean two navigation landmarks and two consumers of
 * `navGroups()` free to drift apart; this way the drawer cannot show a
 * different menu from the desktop sidebar, because it IS the desktop sidebar.
 *
 * The drawer stays a <nav> and does NOT take role="dialog". A dialog role
 * REPLACES the navigation landmark, so a screen-reader operator would trade the
 * one landmark they use to jump between sections for a modal announcement they
 * do not need -- aria-expanded on the trigger, the focus trap and Escape
 * already say what state it is in, and the thing behind the backdrop is not a
 * form waiting on a decision. Modality here is a scroll lock, not a semantic.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useCurrentUser } from '../../api/queries';
import { navGroups } from './navigation';
import { LanguageToggle } from './LanguageToggle';
import { ThemeToggle } from './ThemeToggle';
import { useT } from '../../i18n';

/**
 * Duplicated in global.css, which gates every narrow-width rule on the same
 * string. A media query cannot read a custom property, and the half of the
 * drawer that lives in JS -- the focus trap, the Escape key, the open flag --
 * has to know when the drawer stopped existing.
 *
 * Written as the SAME condition on both sides rather than as its arithmetic
 * complement: a `max-width: 899.98px` here and a `min-width: 900px` there
 * agree everywhere except the hairline between them, which is the kind of
 * disagreement that surfaces once on one device and is never reproduced.
 */
const NARROW_QUERY = '(max-width: 899.98px)';

/** Everything inside the drawer that can hold focus, in tab order. */
const FOCUSABLE = 'a[href], button:not(:disabled)';

export function AppShell() {
  const { signOut, config } = useAuth();
  const me = useCurrentUser();
  const location = useLocation();
  const t = useT();

  const [navOpen, setNavOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const openedByUs = useRef(false);

  const closeNav = useCallback(() => setNavOpen(false), []);

  /*
   * Every navigation closes the drawer.
   *
   * A NavLink click does not unmount the shell, so without this the operator
   * taps a link, the page behind changes, and the menu is still sitting on top
   * of it. `location.key` rather than `pathname`: tapping the link for the page
   * you are already on is still a navigation and must still close, and a page
   * that keeps its own state in the URL (Bear history does) must not be able to
   * leave the drawer open either.
   */
  useEffect(() => {
    setNavOpen(false);
  }, [location.key]);

  /*
   * Crossing back above the break has to clear the STATE, not just the styling.
   * The sidebar re-joins the grid on its own, but a stale `navOpen` would leave
   * the trap below holding focus inside what is now an ordinary, permanently
   * visible sidebar, with no backdrop left to explain why Tab will not leave.
   */
  useEffect(() => {
    const query = globalThis.matchMedia?.(NARROW_QUERY);
    if (!query) return;

    const sync = () => {
      if (!query.matches) setNavOpen(false);
    };

    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!navOpen) return;
    const drawer = sidebarRef.current;
    if (!drawer) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;

      // Re-read on every Tab instead of caching on open: the list is rendered
      // from the dictionary and the close button is display:none above the
      // break, and a cached list could send focus at a node that is no longer
      // laid out. `offsetParent` is the cheap "is this actually rendered" test.
      const items = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null,
      );
      const first = items.at(0);
      const last = items.at(-1);
      if (!first || !last) return;

      // Focus can be outside the drawer entirely -- clicking the backdrop
      // leaves it on <body> -- so pull it back rather than assuming it sits on
      // one of the two ends.
      if (!drawer.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  /*
   * Focus moves into the drawer on open and back to the hamburger on close --
   * but only when this component is what moved it. Without the ref guard the
   * first render of every session would yank focus to a button that is
   * display:none on a desktop, stealing it from whatever the page had.
   */
  useEffect(() => {
    if (navOpen) {
      openedByUs.current = true;
      closeRef.current?.focus();
      return;
    }
    if (!openedByUs.current) return;
    openedByUs.current = false;
    toggleRef.current?.focus();
  }, [navOpen]);

  return (
    <div className="app-shell" data-nav-open={navOpen ? 'true' : undefined}>
      <nav id="app-sidebar" ref={sidebarRef} className="sidebar" aria-label={t.shell.navLabel}>
        {/*
         * The hamburger is underneath the open drawer, so the drawer has to
         * carry its own way out for anyone not using a keyboard and not
         * guessing that the dimmed area is tappable.
         */}
        <button
          type="button"
          ref={closeRef}
          className="btn btn--sm btn--subtle sidebar__close"
          aria-label={t.shell.navClose}
          onClick={closeNav}
        >
          <span aria-hidden="true">✕</span>
        </button>

        <NavLink to="/" className="sidebar__brand">
          {t.shell.brand}
          <small>{t.shell.brandSub}</small>
        </NavLink>

        {navGroups(t).map((group, groupIndex) => (
          <div className="sidebar__group" key={group.label ?? `group-${groupIndex}`}>
            {group.label ? <div className="sidebar__group-label">{group.label}</div> : null}
            {group.items.map((item) => {
              const active =
                location.pathname === item.to ||
                (item.matchPrefix ? location.pathname.startsWith(item.matchPrefix) : false);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    isActive || active ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {navOpen ? (
        <div className="app-shell__backdrop" aria-hidden="true" onClick={closeNav} />
      ) : null}

      <div className="main">
        {config.useDevFixtures ? (
          <div className="dev-banner" role="status">
            {t.shell.devBanner}
          </div>
        ) : null}

        <header className="topbar">
          {/*
           * First in the bar, ahead of the flex:1 spacer -- that is what pins it
           * to the left edge, on the same side as the drawer it opens. It is
           * display:none above the break, so the desktop bar still begins with
           * the spacer and is unchanged.
           */}
          <button
            type="button"
            ref={toggleRef}
            className="btn btn--sm btn--subtle nav-toggle"
            aria-label={t.shell.navToggle}
            aria-expanded={navOpen}
            aria-controls="app-sidebar"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
          </button>

          <div className="topbar__spacer" />
          <span className="small muted topbar__who">
            {me.data ? (
              <>
                {me.data.displayName ?? t.shell.signedIn} · {me.data.role}
                {me.data.playerId ? null : ` · ${t.shell.noLinkedPlayer}`}
              </>
            ) : null}
          </span>
          <ThemeToggle />
          <LanguageToggle />
          <button type="button" className="btn btn--sm btn--subtle" onClick={() => void signOut()}>
            {t.shell.signOut}
          </button>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
